const PAYMENT_ONLY_PAGES = new Set([
  "purchase-video",
  "purchase-audio",
  "purchase-ppv-event",
  "check-out",
  "pricing",
  "single-plan",
  "thank-you",
  "checkout-cancel",
]);

const CONTENT_WATCH_PAGES = new Set([
  "watch-movies",
  "play-audio",
  "watch-live",
]);

const PAYMENT_URL_PARAMS = [
  "videoId",
  "audioId",
  "eventId",
  "channelId",
  "planId",
  "ppvId",
  "paymentType",
];

export function isSitePaymentOnly(settings) {
  return !!settings?.generalSettings?.is_site_payment_only;
}

export function getCurrentPageName() {
  const location = window.location.pathname.split("/").pop() || "index.html";
  const pageName = location.split(".").shift() || "index";
  return pageName === "index" || !pageName ? "index" : pageName;
}

export function hasPaymentUrlParams(search = window.location.search) {
  const params = new URLSearchParams(search);
  return PAYMENT_URL_PARAMS.some((key) => params.get(key));
}

export function isPaymentOnlyPage(pageName = getCurrentPageName()) {
  if (PAYMENT_ONLY_PAGES.has(pageName)) {
    return true;
  }

  if (CONTENT_WATCH_PAGES.has(pageName)) {
    return hasPaymentUrlParams();
  }

  return false;
}

export function shouldAutoShowPayment(generalSettings = {}) {
  return !!generalSettings.is_site_payment_only;
}

export function applyPaymentOnlyStyles(isPaymentPage) {
  document.body.classList.add("payment-only-mode");

  if (!isPaymentPage) {
    return `
      body.payment-only-mode #app-container {
        display: none !important;
      }
    `;
  }

  // On payment pages we keep the player/form containers visible because the
  // payment form (.purchase-card) is injected INTO the player element
  // (#playerClappr). We hide chrome, the actual video/audio player nodes, and
  // sibling content blocks, but keep the injected purchase form visible.
  return `
    body.payment-only-mode,
    body.payment-only-mode html {
      background-color: #ffffff !important;
      color: #000000;
    }

    body.payment-only-mode #app-container header,
    body.payment-only-mode #app-container footer,
    body.payment-only-mode #app-container .entertainment-header,
    body.payment-only-mode #app-container .playtv-header,
    body.payment-only-mode #app-container .entertainment-header-mobile-menu-wrapper,
    body.payment-only-mode #app-container .entertainment-header-logo-menu-wrapper,
    body.payment-only-mode #app-container .entertainment-header-btn-toggle-menu,
    body.payment-only-mode #app-container .watch-movies-wrapper-container > header,
    body.payment-only-mode #app-container .play-audio-wrapper-container > header,
    body.payment-only-mode #app-container .video-player-item.description,
    body.payment-only-mode #app-container .video-player-item.suggestion,
    body.payment-only-mode #app-container .video-player-item.next,
    body.payment-only-mode #app-container .video-player-item.utils,
    body.payment-only-mode #app-container .audio-player-item.description,
    body.payment-only-mode #app-container .audio-player-item.suggestion,
    body.payment-only-mode #app-container .audio-player-item.next,
    body.payment-only-mode #app-container .audio-player-item.utils,
    body.payment-only-mode #app-container .banner-slider,
    body.payment-only-mode #app-container [role="sliderwrapper"],
    body.payment-only-mode #app-container [role="channelsliderwrapper"],
    body.payment-only-mode #app-container .video-tabs,
    body.payment-only-mode #app-container .video-tabs-container {
      display: none !important;
      height: 0 !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: none !important;
      overflow: hidden !important;
    }

    /* Hide the actual video/audio player rendered by Clappr, but keep the
       injected payment UI that lives in the same node. Depending on the active
       gateway the payment UI is either an inline ".purchase-card" (Stripe) or a
       fixed-position ".modal" overlay (PayPal, DPO Pay, Authorize.Net). */
    body.payment-only-mode #app-container #playerClappr > *:not(.purchase-card):not(.modal),
    body.payment-only-mode #app-container .button-play-container {
      display: none !important;
    }

    body.payment-only-mode #app-container,
    body.payment-only-mode #app-container .watch-movies-wrapper-container,
    body.payment-only-mode #app-container .play-audio-wrapper-container,
    body.payment-only-mode #app-container .block-wrapper-container {
      min-height: 100vh;
      background: #ffffff !important;
      background-color: #ffffff !important;
      background-image: none !important;
    }

    body.payment-only-mode #app-container .video-player-container,
    body.payment-only-mode #app-container .audio-player-container {
      display: block !important;
      grid-template-columns: 1fr !important;
    }

    body.payment-only-mode #app-container .video-player-item.player,
    body.payment-only-mode #app-container .audio-player-item.player,
    body.payment-only-mode #app-container #playerClappr {
      display: block !important;
      width: 100% !important;
      height: auto !important;
      min-height: 0 !important;
      background: #ffffff !important;
      background-color: #ffffff !important;
    }

    /* CMS templates often set the player shell to black; strip that in payment-only mode. */
    body.payment-only-mode #app-container .video-player-container,
    body.payment-only-mode #app-container .video-player-container.content-grid,
    body.payment-only-mode #app-container .video-player-item,
    body.payment-only-mode #app-container .audio-player-container,
    body.payment-only-mode #app-container .audio-player-item {
      background: #ffffff !important;
      background-color: #ffffff !important;
      border: none !important;
      box-shadow: none !important;
    }

    /* Clappr leaves a black container even when the video UI is hidden. */
    body.payment-only-mode #app-container #playerClappr .container,
    body.payment-only-mode #app-container #playerClappr [data-player],
    body.payment-only-mode #app-container #playerClappr .player-wrapper,
    body.payment-only-mode #app-container #playerClappr .player-poster,
    body.payment-only-mode #app-container #playerClappr .player-screen,
    body.payment-only-mode #app-container #playerClappr canvas,
    body.payment-only-mode #app-container #playerClappr video {
      display: none !important;
      height: 0 !important;
      min-height: 0 !important;
      background: transparent !important;
    }

    body.payment-only-mode #app-container .subscription-card-wrapper,
    body.payment-only-mode #app-container .form-content-wrapper,
    body.payment-only-mode #app-container .ppv-event-banner-item,
    body.payment-only-mode #app-container #thankyou-container,
    body.payment-only-mode #app-container .purchase-card,
    body.payment-only-mode .modal .purchase-card {
      display: block !important;
      max-width: 720px;
      margin: 24px auto;
    }

    /* Replace top spacing lost when the gateway close button row is hidden. */
    body.payment-only-mode #app-container .purchase-card,
    body.payment-only-mode .modal .purchase-card {
      padding-top: 32px !important;
    }

    /* In payment-only mode the payment form must not be closable. Hide every
       gateway close button (Stripe/PayPal/DPO Pay/Authorize.Net) as well as the
       choice / PPV options / access-code modal close buttons. These selectors
       are intentionally NOT scoped to #app-container because some modals are
       appended directly to <body>. */
    body.payment-only-mode .close-purchase-card,
    body.payment-only-mode .closeStripePurchaseCardBox,
    body.payment-only-mode .closePaypalPurchaseCardBox,
    body.payment-only-mode .closeAuthorizeNetPurchaseCardBox,
    body.payment-only-mode .closeDPOPayPurchaseCardBox,
    body.payment-only-mode .close-payment-choice,
    body.payment-only-mode .close-ppv-options,
    body.payment-only-mode .close-access-code {
      display: none !important;
    }

    /* Modal gateways use a dark scrim; use a clean white backdrop in payment-only mode. */
    body.payment-only-mode .modal#PaypalPurchaseCard,
    body.payment-only-mode .modal#DPOPayPurchaseCard,
    body.payment-only-mode .modal#authorizeNetPurchaseCard {
      background-color: #ffffff !important;
    }
  `;
}
